import { Text } from '@react-pdf/renderer';
import React from 'react';
import useProduct from '../useProduct';
import { Box, Flex } from '../..';
import CircleIcon from '../../CircleIcon';
import abbreviations from './abbreviations.json';
import medalMappings from './medalMappings.json';
import medals from './medalNames/medals.json';
import otherPoints from './medalNames/points-other.json';
import enPoints from './medalNames/points-en.json';

export default function Awards({ product: data, style }) {
  const product = useProduct(data);
  const awardsToRender =
    product.awards?.length > 4
      ? product.awards?.slice(0, 4).concat([
          {
            awardCode: 'EXTRA',
          },
        ])
      : product.awards;
  return (
    <Flex style={{ flexWrap: 'wrap' }}>
      {awardsToRender?.map(({ award, awardCode, medal, medalCode }) => {
        const medalText = getMedalText({ medal, medalCode });
        return (
          <Box key={`${awardCode}_${medalCode}`} style={{ marginRight: 12 }}>
            {awardCode === 'EXTRA' ? (
              <CircleIcon
                value={`+ ${product.awards?.length - 4}`}
                label={'more'}
              />
            ) : (
              <Box
                style={{
                  ...getMedalDesign({ medalCode }),
                  position: 'relative',
                }}
              >
                <Flex
                  style={{
                    flexDirection: 'column',
                    height: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'absolute',
                    left: 2,
                    right: 2,
                  }}
                >
                  {medalText && (
                    <Text
                      style={{
                        fontSize: getMedalTextFontSize({ text: medalText }),
                        textAlign: 'center',
                        marginBottom: 2,
                      }}
                    >
                      {medalText}
                    </Text>
                  )}
                  <Text
                    style={{
                      fontSize: 6,
                      textAlign: 'center',
                      textTransform: 'uppercase',
                    }}
                  >
                    {getAwardName({ award, awardCode })}
                  </Text>
                </Flex>
              </Box>
            )}
          </Box>
        );
      })}
    </Flex>
  );
}

const medalNames = {
  // English needs points with decimals
  en: {
    ...medals,
    ...enPoints,
  },
  // Other languages need points with commas
  // Not sure how to handle locales yet....
  otherLanguages: {
    ...medals,
    ...otherPoints,
  },
};

/**
 * Gets award name using abbreviation lookup.
 * The lookup allows us to optimize the medal / point text for the PDF
 * @param {Object} props
 * @param {String} props.medal - localized medals / points name from GraphQL
 * @param {String} props.medalCode - medal / points code
 * @returns
 */
function getMedalText({ medalCode, medal }) {
  // Fall back to GraphQL value if not in medals file
  // This means that new awards added to Bottlebooks will still be
  // displayed here even if they aren't in the medalNames.
  return medalNames.en[medalCode] || medal;
}

/**
 * Dynamically font sizing to ensure that medals with longer names don't blow out the circle
 * @param {Object} props
 * @param {String} props.text
 * @returns
 */
function getMedalTextFontSize({ text }) {
  if (text.length < 8) return 10;
  return (8 / text.length) * 10;
}

/**
 * Gets award name using abbreviation lookup.
 * The abbreviation lookup allows us to shorten names such as the International
 * Wine Competition to IWC.
 * @param {Object} props
 * @param {String} props.award - localized award name from GraphQL
 * @param {String} props.awardCode - award code
 * @returns
 */
function getAwardName({ award, awardCode }) {
  // Default to award text from GraphQL if not in abbreviations file
  return abbreviations[awardCode] || award;
}

/**
 * Gets medal design
 * @param {Object} props
 * @param {String} props.medalCode
 * @returns {Object}
 */
function getMedalDesign({ medalCode }) {
  const defaultDesign = {
    backgroundColor: '#ffffff',
    border: '1px solid, #0000000',
    height: 50,
    width: 50,
    borderRadius: 25,
  };
  if (!medalCode) return defaultDesign;
  const resolvedColor = medalMappings[medalCode];
  switch (resolvedColor) {
    case 'platinum':
      return { ...defaultDesign, backgroundColor: '#e5e4e2', border: 'none' };
    case 'gold':
      return { ...defaultDesign, backgroundColor: '#DDAA00', border: 'none' };
    case 'silver':
      return { ...defaultDesign, backgroundColor: '#C0C0C0', border: 'none' };
    case 'bronze':
      return { ...defaultDesign, backgroundColor: '#CD7F32', border: 'none' };
    default:
      return defaultDesign;
  }
}
